import React, { useState } from 'react'
import http, { ensureCsrfCookie } from '../../services/http'
import { useTranslation } from '../../hooks/useTranslation'
import { Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'

const STORAGE_KEY = 'bx_newsletter_subscribed'

const NewsletterForm: React.FC = () => {
  const { t, isRTL } = useTranslation()
  const [email, setEmail] = useState('')

  // Check localStorage so the form stays locked after page reload too
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1' ? 'success' : 'idle'
    } catch {
      return 'idle'
    }
  })

  const isLocked = status === 'loading' || status === 'success'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || isLocked) return
    setStatus('loading')
    try {
      await ensureCsrfCookie()
      await http.post('/api/newsletter/subscribe', { email })
      setStatus('success')
      setEmail('')
      try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* storage blocked */ }
    } catch {
      setStatus('error')
    }
  }

  return (
    <form onSubmit={submit} className="w-full max-w-md space-y-3">
      <div className="flex flex-col sm:flex-row items-stretch gap-2.5 w-full">

        {/* مربع الإدخال الممتد */}
        <div className="relative flex-1 min-w-[220px]">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            disabled={isLocked}
            placeholder={t('footer.newsletter.placeholder')}
            className={`w-full h-11 px-4 bg-surface-soft border border-border rounded-lg text-text text-sm placeholder-text-muted transition-all duration-200 focus:outline-none focus:border-gold focus:shadow-[0_0_12px_rgba(212,175,55,0.15)] disabled:opacity-50 disabled:cursor-not-allowed ${
              isRTL ? 'text-right' : 'text-left'
            }`}
          />
        </div>

        {/* زر الاشتراك */}
        <button
          type="submit"
          disabled={isLocked}
          className="h-11 px-5 bg-gold hover:bg-gold-light text-bg font-semibold text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-gold/20 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {status === 'loading' ? (
            <Loader2 size={16} className="animate-spin text-bg" />
          ) : (
            <>
              <span>{t('footer.newsletter.subscribe')}</span>
              <Send size={14} className={`transform transition-transform duration-200 ${isRTL ? 'rotate-180 group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5'}`} />
            </>
          )}
        </button>
      </div>

      {/* تنبيهات الحالة */}
      {status === 'success' && (
        <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-medium animate-fadeIn">
          <CheckCircle2 size={14} />
          <span>{t('footer.newsletter.success')}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-1.5 text-rose-400 text-xs font-medium animate-fadeIn">
          <AlertCircle size={14} />
          <span>{t('footer.newsletter.error')}</span>
        </div>
      )}
    </form>
  )
}

export default NewsletterForm